import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { CachingService, StableCache } from '@nk/caching-lib';
import { PAGE_LIMIT } from '@nk/company/company-view/components/company-role-card/company-role-card.utils';
import {
  AppFeatureKey,
  AppRoleKey,
  COUNTRY_FEATURES,
  CompanyLegalRightCompositeEntity,
  CompanyShareOwnershipCompositeEntity,
  GenderEntity,
  PERSON_FLAG_FEATURES,
  PERSON_FLAG_ROLES,
  PersonAliasEntity,
  PersonCompanyRoleCompositeEntity,
  PersonCompanyRolePersonFlagCompositeEntity,
  PersonCompanyRoleRawEntity,
  PersonEntity,
  PersonFlagCompositeEntity,
  PersonFlagEntity,
  PersonFlagTypeData,
  PersonFlagTypeEntity,
  PersonMasterEntity,
  PersonNameEntity,
  PersonNationalIdentificationEntity,
  SensitiveDataTypeKey,
  SimpleCollection,
} from '@nk/data-entities-lib';
import { DictList } from '@nk/utils-lib';
import { Observable, forkJoin, from, of } from 'rxjs';
import { concatMap, first, map, mergeMap } from 'rxjs/operators';
import { EntityUpdateTimestampService } from './entity-update-timestamp.service';

export interface PersonAliasCompositeEntity {
  person_alias: PersonAliasEntity;
  person: PersonEntity;
  alias_person: PersonEntity;
  alias_person_flag_composites: PersonFlagCompositeEntity[];
  alias_person_company_role_composites: PersonCompanyRoleCompositeEntity[];
  alias_person_company_share_ownership_composites: CompanyShareOwnershipCompositeEntity[];
  alias_person_company_legal_right_composites: CompanyLegalRightCompositeEntity[];
}

@Injectable()
export class PersonService {
  constructor(
    private _analysisApiClientAccess: AnalysisApiClientAccess,
    private _applicationApi: ApplicationApiClient,
    private _cachingService: CachingService,
    private _entityUpdateTimestampService: EntityUpdateTimestampService,
  ) {}

  public personCache = this._cachingService.create(this, this.fetchPerson);
  public fetchPerson(personUuid: string): Observable<PersonEntity> {
    return this._analysisApiClientAccess.get<PersonEntity>(
      `person/${personUuid}`,
      {},
      [AppFeatureKey.base, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public personNameByPersonCache = this._cachingService.create(this, this.fetchPersonNameByPersonCache);
  public fetchPersonNameByPersonCache(personUuid: string): Observable<PersonNameEntity[]> {
    return this._analysisApiClientAccess.get<PersonNameEntity[]>(
      `person/${personUuid}/person-name`,
      null,
      [AppFeatureKey.base, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public personMasterByPerson = this._cachingService.create(this, this.fetchPersonMasterByPerson);
  public fetchPersonMasterByPerson(personUuid: string): Observable<PersonMasterEntity[]> {
    return this._analysisApiClientAccess.get<PersonMasterEntity[]>(
      `person/${personUuid}/person-master`,
      {},
      [AppFeatureKey.base, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public personMasterByUuidCache = this._cachingService.create(this, this.fetchPersonMasterByUuid);
  public fetchPersonMasterByUuid(personMasterUuid: string): Observable<PersonMasterEntity> {
    return this._analysisApiClientAccess.get<PersonMasterEntity>(
      `person-master/${personMasterUuid}`,
      {},
      [AppFeatureKey.base, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public personFlagCompositeByPersonCache = this._cachingService.create(this, this.fetchPersonFlagCompositeByPerson);
  public fetchPersonFlagCompositeByPerson(personUuid: string): Observable<PersonFlagCompositeEntity[]> {
    return this._analysisApiClientAccess.get<PersonFlagCompositeEntity[]>(
      `/person/${personUuid}/person-flag-composite`,
      {},
      [AppFeatureKey.base, ...PERSON_FLAG_FEATURES, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data, ...PERSON_FLAG_ROLES],
    );
  }

  public personFlagCompositeByPersonMasterCache = this._cachingService.create(
    this,
    this.fetchPersonFlagCompositeByPersonMaster,
  );
  public fetchPersonFlagCompositeByPersonMaster(personMasterUuid: string): Observable<PersonFlagCompositeEntity[]> {
    return this._analysisApiClientAccess.get<PersonFlagCompositeEntity[]>(
      `/person-master/${personMasterUuid}/person-flag-composite`,
      {},
      [AppFeatureKey.base, ...PERSON_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...PERSON_FLAG_ROLES],
    );
  }

  public personRoleCompositeCollectionByPersonCache = this._cachingService.create(
    this,
    this.fetchPersonRoleCompositeCollectionByPerson,
  );
  public fetchPersonRoleCompositeCollectionByPerson(
    personUuid: string,
    companyIdentifier?: string,
    currentRoles = true,
    limit: number | null = null,
    offset: number | null = null,
  ): Observable<SimpleCollection<PersonCompanyRoleCompositeEntity>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('current_flag', currentRoles);
    if (companyIdentifier) {
      httpParams = httpParams.append('company_identifier', companyIdentifier);
    }
    if (limit) {
      httpParams = httpParams.append('limit', String(limit));
    }
    if (offset) {
      httpParams = httpParams.append('offset', String(offset));
    }

    return this._analysisApiClientAccess.get<SimpleCollection<PersonCompanyRoleCompositeEntity>>(
      `person/${personUuid}/person-company-role-composite-collection`,
      { params: httpParams },
      [
        AppFeatureKey.base,
        AppFeatureKey.historical_roles,
        AppFeatureKey.norwegian_property_data,
        AppFeatureKey.company_risk_score,
        ...COUNTRY_FEATURES,
      ],
      [AppRoleKey.norwegian_property_data],
    );
  }

  /**
   * @deprecated #2658 - Disable "Raw Official Roles" for Selmer.
   */
  public personCompanyRoleRawListByCompanyCache = this._cachingService.create(
    this,
    this.fetchPersonCompanyRoleRawListByCompany,
  );
  public fetchPersonCompanyRoleRawListByCompany(
    companyUuid: string,
    params?: HttpParams,
  ): Observable<PersonCompanyRoleRawEntity[]> {
    // ! Since this is a beta feature we are checking feature on customer slug here. but this is not the practice in general, should use feature system.
    return this._applicationApi.get<PersonCompanyRoleRawEntity[]>(`company/${companyUuid}/person-company-role-raw`, {
      params: params,
    });
  }

  public personCompanyRolePersonFlagListByCompanyCache = this._cachingService.create(
    this,
    this.fetchPersonCompanyRolePersonFlagListByCompany,
  );
  public fetchPersonCompanyRolePersonFlagListByCompany(
    companyUuid: string,
    currentFlag: boolean,
    companyRoleIdentifiers: string[],
    limit = PAGE_LIMIT,
    offset = 0,
  ): Observable<SimpleCollection<PersonCompanyRolePersonFlagCompositeEntity>> {
    let params = new HttpParams();
    if (currentFlag !== undefined) {
      params = params.append('current_flag', String(currentFlag));
    }
    if (companyRoleIdentifiers?.length) {
      params = params.append('company_role_identifiers', companyRoleIdentifiers.join(','));
    }
    params = params.append('limit', String(limit));
    params = params.append('offset', String(offset));
    return this._analysisApiClientAccess.get<SimpleCollection<PersonCompanyRolePersonFlagCompositeEntity>>(
      `company/${companyUuid}/person-company-role-person-flag-composite-collection`,
      { params },
      [AppFeatureKey.base, AppFeatureKey.historical_roles, ...PERSON_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...PERSON_FLAG_ROLES],
    );
  }

  public streamPersonFlagByPersonUuidList(personUuids: string[]): Observable<DictList<PersonFlagTypeData>> {
    return from(personUuids).pipe(
      concatMap((personUuid) => {
        return forkJoin([this.personFlagTypeListByPersonCache.get(personUuid), of(personUuid)]);
      }),
      mergeMap((payload) => {
        const personFlagTypeDataList = payload[0];
        const personUuid = payload[1];
        return of({
          [personUuid]: personFlagTypeDataList,
        });
      }),
    );
  }

  public personFlagTypeListByPersonCache = this._cachingService.create(this, this.fetchPersonFlagTypeListByPerson);
  public fetchPersonFlagTypeListByPerson(personUuid: string): Observable<PersonFlagTypeData[]> {
    return forkJoin([
      this.personFlagListByPersonCache.get(personUuid).pipe(first()),
      this.personFlagTypeListCache.get().pipe(first()),
    ]).pipe(
      map((payloads) => {
        const [personFlag, personFlagType] = payloads;
        return personFlag.map(
          (m) =>
            new PersonFlagTypeData(
              m,
              personFlagType.find((f) => f.uuid === m.person_flag_type_uuid),
            ),
        );
      }),
    );
  }

  public personFlagListByPersonCache = this._cachingService.create(this, this.fetchPersonFlagListByPerson);
  public fetchPersonFlagListByPerson(personUuid: string): Observable<PersonFlagEntity[]> {
    return this._analysisApiClientAccess.get<PersonFlagEntity[]>(
      `person/${personUuid}/person-flag`,
      {},
      [AppFeatureKey.base, ...PERSON_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...PERSON_FLAG_ROLES],
    );
  }

  public personFlagTypeListCache = this._cachingService.create(this, this.fetchPersonFlagTypeList);
  public fetchPersonFlagTypeList(): Observable<PersonFlagTypeEntity[]> {
    return this._analysisApiClientAccess.get<PersonFlagTypeEntity[]>(
      'person-flag-type',
      {},
      [AppFeatureKey.base, ...PERSON_FLAG_FEATURES],
      [...PERSON_FLAG_ROLES],
    );
  }

  public genderListCache = new StableCache(
    'app.gender',
    this,
    this.fetchGenderList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchGenderList(): Observable<GenderEntity[]> {
    return this._analysisApiClientAccess.get<GenderEntity[]>('gender');
  }

  public personAliasCompositeByPersonCache = this._cachingService.create(this, this.fetchPersonAliasCompositeByPerson);
  public fetchPersonAliasCompositeByPerson(
    personUuid: string,
    includeHistoricRolesFlag: boolean,
  ): Observable<PersonAliasCompositeEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append(
      'composite_fields',
      [
        'person',
        'alias_person',
        'alias_person_flag_composites',
        'alias_person_company_role_composites',
        'alias_person_company_share_ownership_composites',
        'alias_person_company_legal_right_composites',
      ].join(','),
    );
    httpParams = httpParams.append('include_historic_roles_flag', includeHistoricRolesFlag);

    return this._analysisApiClientAccess.get<PersonAliasCompositeEntity[]>(
      `/person/${personUuid}/person-alias-composite`,
      { params: httpParams },
      [
        AppFeatureKey.base,
        AppFeatureKey.historical_roles,
        AppFeatureKey.norwegian_property_data,
        ...PERSON_FLAG_FEATURES,
        ...COUNTRY_FEATURES,
      ],
      [AppRoleKey.norwegian_property_data, ...PERSON_FLAG_ROLES],
    );
  }

  public personNationalIdentificationCache = this._cachingService.create(this, this.fetchPersonNationalIdentification);
  public fetchPersonNationalIdentification(
    personUuid: string,
    appMembershipUUid: string,
  ): Observable<PersonNationalIdentificationEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('app_membership_uuid', appMembershipUUid);
    return this._applicationApi.get<PersonNationalIdentificationEntity[]>(
      `/person/${personUuid}/person-national-identification`,
      { params: httpParams },
      [AppFeatureKey.base],
      [AppRoleKey.person_national_identification],
      [SensitiveDataTypeKey.person_national_identification], // TODO: use the existing system to check this.
    );
  }
}
